import { format, isValid, parseISO, startOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BaseIndicator, NumberOrNullOrND } from 'src/types/wallet';
import { formatNumber, intlFormatNumber } from 'src/utils/number';

/**
 * Retorna a data ISO (yyyy-MM-dd) do início do mês N meses antes da data informada.
 * Útil para calcular janelas de histórico de indicadores.
 */
export function getMonthStartDateBefore(
  isoDate: string,
  monthsBack: number,
): string {
  if (!isoDate) {
    return isoDate;
  }

  const parsedDate = parseISO(isoDate);

  if (!isValid(parsedDate)) {
    return isoDate;
  }

  return format(subMonths(startOfMonth(parsedDate), monthsBack), 'yyyy-MM-dd');
}

type IndicatorType = 'simple' | 'delta' | 'sobre';

type IndicatorConfig = {
  type: IndicatorType;
  column: keyof BaseIndicator;
  relatedColumns?: (keyof BaseIndicator)[];
  label: string;
  relatedLabels?: string[];
};

export type IndicatorHistoryRow = {
  document_date: string;
  data: BaseIndicator | null;
  isEmpty: boolean;
  isExpectedPeriod: boolean;
};

export const INDICATOR_HISTORY_WINDOW_MONTHS = 6;

const DELTA_INDICATOR_MAP: Record<string, keyof BaseIndicator> = {
  delta_dc_pct: 'dc',
  delta_recompras_pct: 'recompras',
  delta_substituicoes_pct: 'substituicoes',
  delta_atrasos_pct: 'atrasos',
  delta_pdd_pct: 'pdd',
};

const SOBRE_INDICATOR_MAP: Record<
  string,
  [keyof BaseIndicator, keyof BaseIndicator]
> = {
  pct_atraso_sobre_dc: ['atrasos', 'dc'],
  pct_caixa_sobre_pl: ['caixa', 'pl'],
  pdd_sobre_dc: ['pdd', 'dc'],
  recompra_sobre_dc: ['recompras', 'dc'],
  pct_pdd_sobre_atrasos: ['pdd', 'atrasos'],
};

const AMORT_PCT_MAP: [keyof BaseIndicator, keyof BaseIndicator] = [
  'amort_total',
  'pl',
];

export function getIndicatorConfig(column: string): IndicatorConfig {
  if (column.includes('delta')) {
    const baseColumn = DELTA_INDICATOR_MAP[column];
    return {
      type: 'delta',
      column: column as keyof BaseIndicator,
      relatedColumns: baseColumn ? [baseColumn] : [],
      label: formatColumnLabel(column),
      relatedLabels: baseColumn ? [formatColumnLabel(baseColumn)] : [],
    };
  }

  if (column.includes('sobre') || column === 'amort_pct') {
    const relatedColumns =
      column === 'amort_pct' ? AMORT_PCT_MAP : SOBRE_INDICATOR_MAP[column];

    return {
      type: 'sobre',
      column: column as keyof BaseIndicator,
      relatedColumns: relatedColumns ? [...relatedColumns] : [],
      label: formatColumnLabel(column),
      relatedLabels: relatedColumns
        ? relatedColumns.map((col) => formatColumnLabel(col))
        : [],
    };
  }

  return {
    type: 'simple',
    column: column as keyof BaseIndicator,
    label: formatColumnLabel(column),
  };
}

export function formatColumnLabel(column: string): string {
  return column
    .replace(/delta/g, 'Δ')
    .replace(/pct/g, '%')
    .replace(/amort_total/g, 'Amortização Total')
    .replace(/pl_cotas/g, 'PL Cotas')
    .replace(/_/g, ' ')
    .toUpperCase()
    .trim();
}

export function formatIndicatorValue(
  value: NumberOrNullOrND,
  valueType: 'percentage' | 'amount' = 'amount',
): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  if (value === 'N/D') {
    return value;
  }

  const formattedNumber = formatNumber(value, 'pt-BR', 0, 2, 'N/A');

  if (valueType === 'percentage') {
    return `${formattedNumber}%`;
  }

  return `R$ ${formattedNumber}`;
}

export function formatIndicatorTooltip(
  value: NumberOrNullOrND,
  valueType: 'percentage' | 'amount' = 'amount',
): string {
  if (value === null || value === undefined) {
    return 'N/A';
  } else if (value === 'N/D') {
    return 'N/D';
  }

  const fullNumber = intlFormatNumber(value, 2);

  if (valueType === 'percentage') {
    return `${fullNumber}%`;
  }

  return `R$ ${fullNumber}`;
}

export function formatReferenceDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM/yyyy', { locale: ptBR });
  } catch {
    return dateString;
  }
}

export function prepareHistoricalData(
  rawData: BaseIndicator[],
  targetDate: string,
  months = INDICATOR_HISTORY_WINDOW_MONTHS,
): IndicatorHistoryRow[] {
  if (!targetDate) {
    return [];
  }

  const sortedData = [...rawData].sort((a, b) => {
    const dateA = new Date(a.document_date).getTime();
    const dateB = new Date(b.document_date).getTime();
    return dateB - dateA;
  });

  const targetDateObj = startOfMonth(parseISO(targetDate));
  const expectedDates = Array.from({ length: months }, (_, index) =>
    format(subMonths(targetDateObj, index), 'yyyy-MM-dd'),
  );

  const rows: IndicatorHistoryRow[] = expectedDates.map((expectedDate) => {
    const dataForPeriod = sortedData.find((item) => {
      const itemDate = format(
        startOfMonth(parseISO(item.document_date)),
        'yyyy-MM-dd',
      );
      return itemDate === expectedDate;
    });

    return {
      document_date: expectedDate,
      data: dataForPeriod || null,
      isEmpty: !dataForPeriod,
      isExpectedPeriod: true,
    };
  });

  return rows;
}

export function isPendingData(
  row: IndicatorHistoryRow,
  targetDate: string,
): boolean {
  if (!row.data || row.isEmpty) {
    return false;
  }

  const expectedDate = format(startOfMonth(parseISO(targetDate)), 'yyyy-MM-dd');
  const dataDate = format(
    startOfMonth(parseISO(row.data.document_date)),
    'yyyy-MM-dd',
  );

  return expectedDate !== dataDate;
}
